// Held-on-time metric across past non-cancelled meetings on a contract.
// Per-row grace via meetingGraceDays so mixed-cadence contracts aggregate
// fairly. pct is null only when no held meetings exist; the sample-size
// is always exposed in the subtitle so 1-of-1 = 100% reads honestly.
// Only meetings with status 'held' contribute to the sample; future or
// scheduled-but-overdue rows have no held-vs-scheduled delta to score.

import type { Meeting } from "../../../../types";
import {
  meetingGraceDays,
  type MeetingGraceConfig,
} from "./meetingGraceDays";

export interface OnTimeRateResult {
  pct: number | null;
  sampleSize: number;
  numerator: number;
}

const MIN_SAMPLE_SIZE = 1;

function daysBetweenUTC(fromIso: string, toIso: string): number {
  const a = new Date(`${fromIso}T00:00:00Z`);
  const b = new Date(`${toIso}T00:00:00Z`);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export function calculateOnTimeRate(
  meetings: Meeting[],
  _today: Date = new Date(),
  graceOverride?: number | MeetingGraceConfig | null,
): OnTimeRateResult {
  // `_today` is part of the public contract for forward-compat with a
  // future "ignore future-dated heldDate" guard; current logic is purely
  // held-vs-scheduled delta + grace, so the parameter is unused for now.
  void _today;
  let sampleSize = 0;
  let numerator = 0;

  for (const m of meetings) {
    if (m.status !== "held") continue;
    if (!m.heldDate) continue;
    sampleSize += 1;
    const grace = meetingGraceDays(m.recurrenceRule, graceOverride);
    const delta = daysBetweenUTC(m.date, m.heldDate);
    if (delta <= grace) numerator += 1;
  }

  if (sampleSize < MIN_SAMPLE_SIZE) {
    return { pct: null, sampleSize, numerator };
  }
  const pct = Math.round((numerator / sampleSize) * 100);
  return { pct, sampleSize, numerator };
}
